import React, { useState, useEffect, useRef } from "react";
import styles from './formvenda.module.css';
const cx = (...names) => names.filter(Boolean).map((n) => styles[n] || n).join(' ');
import ui from '../../styles/ui.module.css';
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Calculadora from "../Calculadora/index";
import { CgCalculator } from "react-icons/cg";
import { FiX } from "react-icons/fi";
import api from "../../services/api";
import ConfirmModal from "../ConfirmModal";


const FormularioHookForm = ({ formData = {}, onNewSubmission, toggleFormulario, onSwitchOperacao, onImportarVendas }) => {
  // 1. Configura o React Hook Form, usando defaultValues vazios
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      data: "",
      compraBobDia: "",
      nomeBRL: "",
      idCliente: null,
      valorBRL: "",
      nomeBOB: "",
      valorBOB: "",
      taxaDia: "",
      lucroBOB: "",
      lucroBRL: "",
      percBRL: "",
    },
  });

  // 2. Quando 'formData' muda, reseta o formulário para esses valores
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      // Se quiser forçar conversão de tipos, pode ajustar aqui:
      reset({
        data: formData.data || "",
        compraBobDia: formData.compraBobDia ?? "",
        nomeBRL: formData.nomeBRL || "",
        idCliente: formData.idCliente ?? null,
        valorBRL: formData.valorBRL ?? "",
        nomeBOB: formData.nomeBOB || "",
        valorBOB: formData.valorBOB ?? "",
        taxaDia: formData.taxaDia ?? "",
        lucroBOB: formData.lucroBOB ?? "",
        lucroBRL: formData.lucroBRL ?? "",
        percBRL: formData.percBRL ?? "",
      });
    }
  }, [formData, reset]);

  // Estados locais para autocomplete
  const [sugestoes, setSugestoes] = useState([]);
  const [todosClientes, setTodosClientes] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const [sugestaoSelecionada, setSugestaoSelecionada] = useState(-1);
  const clienteInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = Boolean(formData && formData.id);

  // Estados para controlar a exibição da Calculadora
  const [showCalculadora, setShowCalculadora] = useState(false);
  const [campoDestino, setCampoDestino] = useState("");

  // 3. Observa o valor do campo nomeBRL
  const nomeBRLValue = watch("nomeBRL");
  const idClienteValue = watch("idCliente");

  // 4. Carrega a lista de clientes uma vez (cache local)
  useEffect(() => {
    (async () => {
      try {
        setClientesLoading(true);
        const res = await api.get("/clientes");
        const arr = Array.isArray(res.data) ? res.data : [];
        setTodosClientes(arr);
      } catch (erro) {
        toast.error("Erro ao carregar clientes para autocomplete");
        console.error("Erro ao carregar clientes:", erro);
      } finally {
        setClientesLoading(false);
      }
    })();
  }, []);

  // 5. Função para filtrar sugestões a partir do cache local
  const buscarSugestoes = (texto) => {
    if (!texto || texto.trim() === "") {
      setSugestoes([]);
      setMostrarSugestoes(false);
      return;
    }
    const textoLC = texto.toLowerCase();
    const textoDigits = texto.replace(/\D/g, '');
    const filtradas = (todosClientes || [])
      .filter((c) => {
        const nomeOk = String(c.NOME || '').toLowerCase().includes(textoLC);
        const cpfDigits = String(c.CPF || '').replace(/\D/g, '');
        const cpfOk = textoDigits.length >= 3 && cpfDigits.includes(textoDigits);
        return nomeOk || cpfOk;
      })
      .slice(0, 20) // limita a 20 para performance/UX
      .map((c) => ({
        id: c.ID,
        nome: c.NOME,
        cpf: c.CPF || '',
        fone: c.WHATSAPP_NUMERO || c.TELEFONE || '',
      }));
    setSugestoes(filtradas);
    setMostrarSugestoes(true);
    setSugestaoSelecionada(-1);
  };

  // 6/7. Atualiza sugestões imediatamente enquanto digita
  useEffect(() => {
    buscarSugestoes(nomeBRLValue);
  }, [nomeBRLValue, todosClientes]);

  // 8. Lida com seleção de sugestão (clique ou Enter)
  const selecionarCliente = (cliente) => {
    setValue("nomeBRL", cliente.nome);
    setValue("idCliente", cliente.id);
    setMostrarSugestoes(false);
    setSugestaoSelecionada(-1);
    if (clienteInputRef.current) {
      clienteInputRef.current.focus();
    }
  };

  // 9. Navegação por teclado nas sugestões
  const handleKeyDownAutocomplete = (e) => {
    if (mostrarSugestoes && sugestoes.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSugestaoSelecionada((prev) =>
          prev < sugestoes.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSugestaoSelecionada((prev) =>
          prev > 0 ? prev - 1 : sugestoes.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (sugestaoSelecionada >= 0) {
          selecionarCliente(sugestoes[sugestaoSelecionada]);
        }
      } else if (e.key === "Escape") {
        setMostrarSugestoes(false);
      }
    }
  };

  // 10. Fecha sugestões ao clicar fora do autocomplete
  useEffect(() => {
    const onDocClick = (e) => {
      if (!autocompleteRef.current) return;
      if (!autocompleteRef.current.contains(e.target)) {
        setMostrarSugestoes(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // 11. Quando usuário digitar no nome, anulamos idCliente até que selecione
  const handleNomeBRLChange = (fieldOnChange) => (e) => {
    fieldOnChange(e);
    if (idClienteValue) setValue('idCliente', null, { shouldValidate: true });
    const cur = e?.target?.value || '';
    buscarSugestoes(cur);
  };

  // 12. Quando o formulário for enviado
  const doSubmit = async (data) => {
    // Validação básica: verifica campos obrigatórios
    if (!data.nomeBRL || !data.valorBRL || !data.data || !data.compraBobDia) {
      toast.warn("Por favor, preencha todos os campos obrigatórios.");
      return;
    }
    // Exige cliente selecionado (idCliente)
    if (!data.idCliente) {
      toast.warn("Selecione um cliente válido na lista (obrigatório).");
      return;
    }

    // Cálculos financeiros
    const toNum = (v) => {
      if (v === null || v === undefined) return 0;
      const s = String(v).replace(',', '.');
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : 0;
    };
    const compraBobDiaNum = toNum(data.compraBobDia);
    const valorBOBNum = toNum(data.valorBOB);
    const valorBRLNum = toNum(data.valorBRL);

    const taxaDia = valorBRLNum !== 0 ? valorBOBNum / valorBRLNum : 0;
    const lucroBOB = valorBRLNum * compraBobDiaNum - valorBOBNum;
    const lucroBRL = compraBobDiaNum !== 0 ? lucroBOB / compraBobDiaNum : 0;
    const percBRL = valorBRLNum !== 0 ? (lucroBRL / valorBRLNum) * 100 : 0;

    const newEntry = {
      data: data.data,
      compraBobDia: compraBobDiaNum,
      nomeBRL: data.nomeBRL,
      idCliente: data.idCliente,
      valorBRL: valorBRLNum,
      nomeBOB: data.nomeBOB,
      valorBOB: valorBOBNum,
      taxaDia: taxaDia.toFixed(2),
      lucroBOB: lucroBOB.toFixed(2),
      lucroBRL: lucroBRL.toFixed(2),
      percBRL: percBRL.toFixed(2),
    };

    try {
      setIsSubmitting(true);
      if (typeof onNewSubmission === 'function') {
        await onNewSubmission(newEntry, 'VENDA');
      }
      // Limpa formulário; se estava em edição, fecha
      const empty = { data: '', compraBobDia: '', nomeBRL: '', idCliente: null, valorBRL: '', nomeBOB: '', valorBOB: '', taxaDia: '', lucroBOB: '', lucroBRL: '', percBRL: '' };
      setTimeout(() => {
        reset(empty);
        if ((formData && formData.id) && typeof toggleFormulario === 'function') toggleFormulario();
        if (!(formData && formData.id) && clienteInputRef.current) clienteInputRef.current.focus();
      }, 0);
    } catch (erro) {
      console.error("Erro ao enviar os dados do formulário:", erro);
      toast.error(erro?.response?.data?.error || erro?.message || "Erro ao lançar venda");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = (data) => {
    setPendingData(data);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (isSubmitting) return;
    setConfirmOpen(false);
  };

  const handleConfirm = async () => {
    if (!pendingData || isSubmitting) return;
    setConfirmOpen(false);
    await doSubmit(pendingData);
  };

  // 10. Funções para abrir/fechar a Calculadora
  const abrirCalculadora = (campo) => {
    setCampoDestino(campo);
    setShowCalculadora(true);
  };

  const aoFecharCalculadora = (resultado) => {
    if (resultado !== null) {
      setValue(campoDestino, resultado, { shouldValidate: true, shouldDirty: true });
    }
    setShowCalculadora(false);
    setCampoDestino("");
  };

  return (
    <div className={styles['venda-form-compovend']}>
      <ConfirmModal
        open={confirmOpen}
        message="Confirmar lançamento desta venda?"
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
        loading={isSubmitting}
        confirmClassName={`${ui.butdesktop} ${ui.btnPrimary}`}
        cancelClassName={ui.butdesktop}
      />
      <button
          type="button"
          className={(ui.butfechar)}
          onClick={() => typeof toggleFormulario === 'function' && toggleFormulario()}
          aria-label="Fechar formulário de venda"
        >
          <FiX size={18} />
        </button>
      
        <h3 className={cx(ui.titg)}>{isEditing ? "Editar venda" : "Lançar vendas do dia"}</h3>
      

      <div className={styles['venda-compra']}>
        <button className={`${styles['btn-venda']} ${styles['active']}`} type="button">
          Venda
        </button>
        <button className={styles['btn-compra']} type="button" onClick={() => typeof onSwitchOperacao === 'function' && onSwitchOperacao('COMPRA')}>
          Compra
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles['venda-formulario']}>
        <div className={styles['venda-form-toolbar']}>
          <button
            type="button"
            className={`${ui.butdesktop} ${ui.btnSecondary}`}
            onClick={() => typeof onImportarVendas === 'function' && onImportarVendas()}
          >
            Importar CSV Vendas
          </button>
        </div>
        {/* Data e Compra (BOB por dia) */}
        <div className={styles['venda-dataecompra']}>
          <div>
            <label className={`${ui.labdesktop}`}>Data</label>
            <input
              className={`${ui.inpdesktop}`}
              type="date"
              {...register("data", { required: "Data é obrigatória" })}
            />
            {errors.data && <span>{errors.data.message}</span>}
          </div>
          <div>
            <label className={`${ui.labdesktop}`}>T.Compra BOB</label>
            <input
              className={`${ui.inpdesktop}`}
              type="text"
              placeholder="Taxa de compra (BOB/dia)"
              title="Taxa de compra do dia (BOB por unidade)"
              {...register("compraBobDia", {
                required: "Compra é obrigatória",
                pattern: {
                  value: /^\d+(?:[\.,]\d{1,2})?$/,
                  message: "Use até 2 casas (ponto ou vírgula)",
                },
              })}
            />
            {errors.compraBobDia && (
              <span>{errors.compraBobDia.message}</span>
            )}
          </div>
        </div>

        {/* Autocomplete Cliente BRL e Valor BRL */}
        <div className={styles['venda-nomevalor']}>
          <div className={styles['venda-autocomplete']} style={{ position: "relative" }} ref={autocompleteRef}>
            <label className={`${ui.labdesktop}`}>Nome BRL</label>
            <Controller
              name="nomeBRL"
              control={control}
              rules={{ required: "Cliente BRL é obrigatório" }}
              render={({ field }) => (
                <input
                  className={`${ui.inpdesktop}`}
                  type="text"
                  placeholder="Cliente BRL"
                  {...field}
                  onChange={handleNomeBRLChange(field.onChange)}
                  onBlur={() => setTimeout(() => setMostrarSugestoes(false), 120)}
                  onFocus={() => {
                    const cur = clienteInputRef.current?.value || '';
                    if (cur.trim().length > 0) buscarSugestoes(cur);
                  }}
                  onKeyDown={handleKeyDownAutocomplete}
                  ref={(e) => {
                    field.ref(e);
                    clienteInputRef.current = e;
                  }}
                />
              )}
            />
            {errors.nomeBRL && <span>{errors.nomeBRL.message}</span>}

            {mostrarSugestoes && (
              <ul className={styles['venda-sugestoes']}>
                {clientesLoading && (
                  <li className={styles['venda-sugestao-item']}>Carregando clientes...</li>
                )}
                {!clientesLoading && sugestoes.length === 0 && (clienteInputRef.current?.value || '').trim().length > 0 && (
                  <li className={styles['venda-sugestao-item']}>Nenhum cliente encontrado</li>
                )}
                {!clientesLoading && sugestoes.map((cliente, index) => (
                  <li
                    key={cliente.id}
                    className={`${styles['venda-sugestao-item']} ${index === sugestaoSelecionada ? styles.selecionada : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selecionarCliente(cliente);
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong>{cliente.nome}</strong>
                      <span style={{ fontSize: 12, opacity: 0.8 }}>
                        CPF: {(cliente.cpf || '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                        {cliente.fone ? ` · Fone: ${cliente.fone}` : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles['divvendainputvalorbrl']}>
            <label className={`${ui.labdesktop}`}>Valor BRL</label>
            <div className={styles['vendainputvalorbrl']}>
              <input
                className={`${ui.inpdesktop}`}
                type="text"
                placeholder="Valor BRL"
                {...register("valorBRL", {
                  required: "Valor BRL é obrigatório",
                  pattern: {
                    value: /^\d+(?:[\.,]\d{1,2})?$/,
                    message: "Use até 2 casas (ponto ou vírgula)",
                  },
                })}
              />
              {errors.valorBRL && <span>{errors.valorBRL.message}</span>}
              <button
                type="button"
                onClick={() => abrirCalculadora("valorBRL")}
              className={`${ui.calcbtn} ${styles['calc-inline']}`}
                aria-label="Abrir calculadora para Valor BRL"
              >
                <CgCalculator size={20} />
              </button>
              </div>
          </div>
        </div>

        {/* Cliente BOB e Valor BOB */}
        <div className={styles['venda-nomevalor']}>
          <div className={styles['venda-autocomplete']}>
            <label className={`${ui.labdesktop}`}>Nome BOB</label>
            <input
              className={`${ui.inpdesktop}`}
              type="text"
              placeholder="Cliente BOB"
              {...register("nomeBOB")}
            />
          </div>
          <div className={styles['divvendainputvalorbrl']}>
            <label className={`${ui.labdesktop}`}>Valor BOB</label>
            <div className={styles['vendainputvalorbrl']}>
              <input
                className={`${ui.inpdesktop}`}
                type="text"
                placeholder="Valor BOB"
                {...register("valorBOB", {
                  pattern: {
                    value: /^\d+(?:[\.,]\d{1,2})?$/,
                    message: "Use até 2 casas (ponto ou vírgula)",
                  },
                })}
              />
              <button
                type="button"
                onClick={() => abrirCalculadora("valorBOB")}
              className={`${ui.calcbtn} ${styles['calc-inline']}`}
                aria-label="Abrir calculadora para Valor BOB"
              >
                <CgCalculator size={20} />
              </button>
              {errors.valorBOB && <span>{errors.valorBOB.message}</span>}
            </div>
          </div>          
        </div>

        {/* Botão Salvar */}
        <div className={`${ui.divbutsalvar}`}>
          <button className={`${ui.butdesktop} ${ui.btnPrimary}`} type="submit">{(formData && formData.id) ? 'Atualizar' : 'Salvar'}</button>
        </div>
      </form>

      {/* Overlay para a Calculadora */}
      {showCalculadora && (
        <div
          className={`${ui.divnegratelatotal}`}
        >
          <Calculadora onClose={(res) => aoFecharCalculadora(res)} />
        </div>
      )}
    </div>
  );
};

export default FormularioHookForm;
